import * as THREE from 'three';
import type { System } from '../../systems';
import type { EntityRegistry } from '../../entities';
import type { PlayerShip } from '../ships';
import { palette } from '../../visual';
import { Asteroid } from './Asteroid';
import type { AsteroidKind } from './Asteroid';
import type { AsteroidPoolDef } from './SectorBuilder';
import { getPoolCenter, spawnPoolAsteroid } from './SectorBuilder';

export interface AsteroidRespawnerDependencies {
  readonly entities: EntityRegistry;
  readonly getPlayerShip: () => PlayerShip | null;
  /** Target population for asteroids that don't belong to any pool. */
  readonly globalTargetCount: number;
  /** Per-pool definitions (planet rings, orbital belts). */
  readonly pools: readonly AsteroidPoolDef[];
}

// Mirrors the spawn weights in SectorBuilder so respawned asteroids match
// the original sector composition. Keeping them in sync by hand is fine —
// neither is shared elsewhere and they're tuned together.
const KIND_GOLD_END = 0.05;
const KIND_URANIUM_END = KIND_GOLD_END + 0.12;
const KIND_PLASMA_END = KIND_URANIUM_END + 0.10;
const KIND_ICE_END = KIND_PLASMA_END + 0.15;
const KIND_IRON_END = KIND_ICE_END + 0.30;

function rollKind(): AsteroidKind {
  const roll = Math.random();
  if (roll < KIND_GOLD_END) return 'gold';
  if (roll < KIND_URANIUM_END) return 'uranium';
  if (roll < KIND_PLASMA_END) return 'plasma';
  if (roll < KIND_ICE_END) return 'ice';
  if (roll < KIND_IRON_END) return 'iron';
  return 'quartz';
}

const MIN_SPAWN_DIST = 2500;
const MAX_SPAWN_DIST = 23000;
const CAMERA_AVOID_DIST = 1200;
const POOL_AVOID_DIST = 220;
const RESPAWN_DELAY = 1.1;

// Deficit thresholds (fraction of target population missing) that drive spawn pacing.
const URGENT_DEFICIT = 0.4;     // → one spawn per frame, no delay
const EMERGENCY_DEFICIT = 0.7;  // → burst spawn per frame
const EMERGENCY_BURST = 4;

interface PendingPool {
  readonly def: AsteroidPoolDef;
  pending: number;
}

interface CountSnapshot {
  total: number;
  global: number;
  byPool: Map<string, number>;
}

export class AsteroidRespawner implements System {
  readonly id = 'asteroid-respawner';

  private globalPending = 0;
  private timer = 0;
  private trackedCount = 0;
  private readonly poolStates = new Map<string, PendingPool>();

  constructor(private readonly deps: AsteroidRespawnerDependencies) {
    for (const pool of deps.pools) {
      this.poolStates.set(pool.id, { def: pool, pending: 0 });
    }
  }

  update(dt: number): void {
    const counts = this.countAsteroids();
    this.trackedCount = counts.total;
    this.absorbDeficit(counts);

    if (this.totalPending() <= 0) {
      this.timer = 0;
      return;
    }

    const deficit = this.worstDeficit(counts);

    if (deficit >= EMERGENCY_DEFICIT) {
      // Any pool (or the global pool) is dangerously empty — refill several
      // per frame so the area never *feels* abandoned to the player.
      const burst = Math.min(EMERGENCY_BURST, this.totalPending());
      for (let i = 0; i < burst; i++) {
        if (!this.spawnOne()) break;
      }
      this.timer = 0;
    } else if (deficit >= URGENT_DEFICIT) {
      // One spawn per frame, no delay.
      this.spawnOne();
      this.timer = 0;
    } else {
      // Comfortably populated — pace spawns out so they fade in instead of popping.
      this.timer -= dt;
      if (this.timer <= 0) {
        this.spawnOne();
        this.timer = RESPAWN_DELAY * (0.6 + Math.random() * 0.8);
      }
    }
  }

  getTrackedCount(): number {
    return this.trackedCount;
  }

  /** Convert "alive count below target" into pending spawn work. */
  private absorbDeficit(counts: CountSnapshot): void {
    const globalMissing =
      this.deps.globalTargetCount - counts.global - this.globalPending;
    if (globalMissing > 0) this.globalPending += globalMissing;

    for (const state of this.poolStates.values()) {
      const have = counts.byPool.get(state.def.id) ?? 0;
      const missing = state.def.count - have - state.pending;
      if (missing > 0) state.pending += missing;
    }
  }

  private totalPending(): number {
    let n = this.globalPending;
    for (const state of this.poolStates.values()) n += state.pending;
    return n;
  }

  /** Worst deficit ratio across all populations, in [0, 1]. */
  private worstDeficit(counts: CountSnapshot): number {
    let worst = 0;
    if (this.deps.globalTargetCount > 0) {
      const missing = Math.max(0, this.deps.globalTargetCount - counts.global);
      worst = Math.max(worst, missing / this.deps.globalTargetCount);
    }
    for (const state of this.poolStates.values()) {
      if (state.def.count <= 0) continue;
      const have = counts.byPool.get(state.def.id) ?? 0;
      const missing = Math.max(0, state.def.count - have);
      worst = Math.max(worst, missing / state.def.count);
    }
    return worst;
  }

  private countAsteroids(): CountSnapshot {
    const byPool = new Map<string, number>();
    let total = 0;
    let global = 0;
    for (const entity of this.deps.entities.all().values()) {
      if (!(entity instanceof Asteroid) || !entity.isAlive) continue;
      total++;
      if (entity.poolId === null) {
        global++;
      } else {
        byPool.set(entity.poolId, (byPool.get(entity.poolId) ?? 0) + 1);
      }
    }
    return { total, global, byPool };
  }

  /** Spawn the most-needed population. Returns false only if no spawn could
   *  be placed (e.g. global spawn ran out of valid attempts). */
  private spawnOne(): boolean {
    // Pick the pool with the largest pending count first — that's the one
    // most at risk of going visibly empty.
    let pickedPool: PendingPool | null = null;
    for (const state of this.poolStates.values()) {
      if (state.pending <= 0) continue;
      if (!pickedPool || state.pending > pickedPool.pending) pickedPool = state;
    }

    const preferPool =
      pickedPool !== null &&
      (this.globalPending <= 0 || pickedPool.pending >= this.globalPending);

    if (preferPool && pickedPool) {
      const asteroid = spawnPoolAsteroid(pickedPool.def);
      this.deps.entities.add(asteroid);
      pickedPool.pending--;
      return true;
    }

    if (this.globalPending > 0) {
      const pos = this.pickGlobalSpawn();
      if (!pos) return false;
      const size = rollSize();
      const kind = rollKind();
      const asteroid = new Asteroid({
        position: pos,
        size,
        detail: size > 5 ? 2 : 1,
        tint: randomTint(),
        kind
      });
      this.deps.entities.add(asteroid);
      this.globalPending--;
      return true;
    }

    return false;
  }

  private pickGlobalSpawn(): THREE.Vector3 | null {
    const player = this.deps.getPlayerShip();
    const playerPos = player?.object3D.position ?? null;

    // First pass — strict camera + pool avoidance.
    for (let attempt = 0; attempt < 8; attempt++) {
      const pos = this.sampleGlobalPoint();
      if (playerPos && pos.distanceTo(playerPos) < CAMERA_AVOID_DIST) continue;
      if (this.isInsideAnyPool(pos)) continue;
      return pos;
    }
    // Fallback — pool avoidance only, drop the camera buffer. Better to spawn
    // somewhere visible than to leave the deep-drift population starved.
    for (let attempt = 0; attempt < 6; attempt++) {
      const pos = this.sampleGlobalPoint();
      if (this.isInsideAnyPool(pos)) continue;
      return pos;
    }
    // Last resort — drop *all* avoidance so we never silently fail.
    return this.sampleGlobalPoint();
  }

  private sampleGlobalPoint(): THREE.Vector3 {
    const theta = Math.random() * Math.PI * 2;
    const u = Math.random() * 2 - 1;
    const sinPhi = Math.sqrt(1 - u * u);
    const r =
      MIN_SPAWN_DIST +
      Math.pow(Math.random(), 0.6) * (MAX_SPAWN_DIST - MIN_SPAWN_DIST);
    return new THREE.Vector3(
      r * sinPhi * Math.cos(theta),
      r * u * 0.55,
      r * sinPhi * Math.sin(theta)
    );
  }

  private isInsideAnyPool(pos: THREE.Vector3): boolean {
    for (const state of this.poolStates.values()) {
      getPoolCenter(state.def, _poolCenterTmp);
      if (pos.distanceTo(_poolCenterTmp) < state.def.outerRadius + POOL_AVOID_DIST) {
        return true;
      }
    }
    return false;
  }
}

const _poolCenterTmp = new THREE.Vector3();

function rollSize(): number {
  const r = Math.random();
  if (r < 0.05) return 7 + Math.random() * 5;
  if (r < 0.22) return 3.5 + Math.random() * 3;
  return 1.4 + Math.random() * 2.4;
}

function randomTint(): number {
  const r = Math.random();
  if (r < 0.1) return palette.asteroid.accent;
  if (r < 0.22) return palette.asteroid.warm;
  return palette.asteroid.body;
}
